/**
 * TeamsV3 — « Archer for Teams » (route `/entreprises`).
 *
 * Transposition iso-pixel de l'export Claude Design validé par Romain le
 * 30/09/2026, `design-refs/for-teams-v3/` (bundle.html = la référence,
 * template.html = le DOM). Le corps de `<main>` ci-dessous EST le DOM de
 * template.html : mêmes balises, mêmes classes, mêmes styles inline, même
 * ordre, y compris sa forme : `div.teams-page` se referme après la 4e
 * section, « rh », « temoignage », « conseiller » et « faq » sont ses sœurs.
 * Il a été produit par conversion mécanique
 * (design-refs/for-teams-v3/conversion/gen.py), pas retapé ; les textes
 * vivent dans `i18n/teamsV3.json`, le français y est copié de la référence
 * au caractère près.
 *
 * Écarts de forme imposés par React, sans effet sur le rendu au repos :
 *   - `<image-slot>` → `FTSlot` : la géométrie que le composant image-slot
 *     du bundle calcule en JS (hôte `aspect-ratio: 3/2` sauf dimensions
 *     explicites, cadre absolu `overflow:hidden`, rond pour
 *     `shape="circle"` ; image absolue centrée par translate(-50 %, -50 %),
 *     largeur et hauteur en % du cadre pour le couvrir).
 *   - Le formulaire est contrôlé (valeurs vides au repos) ; il porte en plus
 *     le champ anti-robot hors écran (`.tp-hp`, règle de la référence), le
 *     message d'erreur et la confirmation, qui n'existent pas au repos.
 *   - La FAQ reste en `<details>/<summary>` natifs, sans état React.
 *   - Les six `hyphens: auto` inline de la référence sont retirés : inertes
 *     dans le bundle (document sans langue, aucun mot coupé), ils
 *     couperaient des mots dans l'app, où `<html lang>` est posé.
 *   - Les cadres des deux premiers portraits du podcast portent
 *     `will-change: transform` : dans le bundle, ils chevauchent les
 *     contrôles cachés de l'éditeur image-slot et Chromium les rastérise
 *     dans leur propre couche. Sans elle, 1 à 8 niveaux d'écart sur leurs
 *     pixels ; avec, 0.
 *
 * Liens : « Parler à un conseiller » (#conseiller) et « Voir comment ça
 * marche » (#fonctionnement) sont des ancres natives ; « Écouter les
 * podcasts » (`href="#"`) est inerte, la page arrivera plus tard.
 *
 * Formulaire : même destination que l'ancienne page, `POST /api/b2b/leads`
 * (collection `b2b_leads`, notification à l'équipe, accusé au prospect).
 * Le téléphone, nouveau dans la référence, part avec la demande.
 *
 * Chrome : header (`PublicHeader`, mode `authCta="dashboard"`), footer
 * (`SiteFooterV2`) et bouton WhatsApp sont les composants communs du site
 * (décision Romain), hors du `<main>` auquel toute la feuille est limitée.
 *
 * L'ancienne page (`TeamsPage`) reste dans le dépôt, non routée.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import PublicHeader from '../components/PublicHeader';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import PageHead from '../components/seo/PageHead';
import { getPageMetadata } from '../lib/seo/metadata';
import { getAttribution } from '../utils/attribution';
import { useSiteLanguage } from '../hooks/useSiteLanguage';
import { createT } from '../i18n/createT';
import dict from '../i18n/teamsV3.json';
// Confirmation, erreurs et champ anti-robot : les textes de l'ancienne
// page, repris tels quels (mêmes clés, mêmes trois langues).
import formDict from '../i18n/teams.json';
import '../styles/teams-v3.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

/**
 * L'image-slot du bundle, sans l'éditeur : hôte bloc relatif (le `:host`
 * du composant : `width:100%; height:100%; aspect-ratio:3/2`, puis le style
 * de l'élément), cadre absolu qui rogne, image absolue centrée,
 * dimensionnée pour couvrir le cadre (même calcul que le composant :
 * échelle = max(cadre/image) sur les deux axes, exprimée en % du cadre).
 * `object-fit` seul ne donne pas le même arrondi de pixels.
 */
function FTSlot({ src, shape, hostStyle, ...rest }) {
  const hostRef = useRef(null);
  const imgRef = useRef(null);
  useEffect(() => {
    const host = hostRef.current;
    const img = imgRef.current;
    if (!host || !img) return undefined;
    const apply = () => {
      const iw = img.naturalWidth; const ih = img.naturalHeight;
      const fw = host.clientWidth; const fh = host.clientHeight;
      if (!iw || !ih || !fw || !fh) return;
      const base = Math.max(fw / iw, fh / ih);
      img.style.width = `${(iw * base / fw) * 100}%`;
      img.style.height = `${(ih * base / fh) * 100}%`;
      img.style.objectFit = '';
    };
    img.addEventListener('load', apply);
    if (img.complete) apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(host);
    return () => { img.removeEventListener('load', apply); if (ro) ro.disconnect(); };
  }, [src]);
  return (
    <span
      ref={hostRef}
      className="ftv3-slot"
      {...rest}
      style={{ display: 'block', position: 'relative', width: '100%', height: '100%', aspectRatio: '3 / 2', ...hostStyle }}
    >
      <span
        className="ftv3-slot-frame"
        style={{ display: 'block', position: 'absolute', inset: 0, overflow: 'hidden', background: 'rgba(127,127,127,.08)', borderRadius: shape === 'circle' ? '50%' : undefined }}
      >
        <img
          ref={imgRef}
          src={src}
          alt=""
          draggable={false}
          style={{ position: 'absolute', display: 'inline', maxWidth: 'none', transform: 'translate(-50%,-50%)', width: '100%', height: '100%', left: '50%', top: '50%', objectFit: 'cover', userSelect: 'none' }}
        />
      </span>
    </span>
  );
}

// Liens « # » de la référence : inertes, aucune navigation.
const inert = (ev) => ev.preventDefault();

/** Champ anti-robot : hors écran (`.tp-hp`) et hors parcours clavier. */
function Honeypot({ t, value, onChange }) {
  return (
    <div aria-hidden="true" className="tp-hp">
      <label htmlFor="tp-website">{t('form.honeypot')}</label>
      <input id="tp-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={value} onChange={onChange} />
    </div>
  );
}

/** Erreur de saisie ou d'envoi, au gabarit des champs de la page. */
function FormError({ text }) {
  return (
    <div role="alert" data-testid="teams-form-error"
         style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', fontWeight: 500, lineHeight: 1.5, color: '#B91C1C' }}>
      {text}
    </div>
  );
}

/** Confirmation : la demande est enregistrée (`b2b_leads`). */
function TeamsFormSent({ t }) {
  return (
    <div role="status" data-testid="teams-form-success"
         style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '10px', padding: '12px 0 4px' }}>
      <span aria-hidden="true" style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#DCFCE7', color: '#15803D', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </span>
      <span style={{ fontFamily: "'Inter Tight', Inter, -apple-system, sans-serif", fontSize: '22px', fontWeight: 700, letterSpacing: '-0.028em', lineHeight: 1.2, color: '#0A0A0F' }}>
        {t('form.sentTitle')}
      </span>
      <span style={{ fontSize: '14.5px', fontWeight: 400, lineHeight: 1.55, color: '#4B5563' }}>
        {t('form.sentBody')}
      </span>
    </div>
  );
}

const EMPTY_FORM = { company: '', name: '', email: '', phone: '', headcount: '', message: '', website: '' };

export default function TeamsV3() {
  const [language] = useSiteLanguage();
  const t = useMemo(() => createT(dict, language), [language]);
  const tForm = useMemo(() => createT(formDict, language), [language]);
  const metadata = useMemo(() => getPageMetadata('teams', language), [language]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | loading | sent | error
  // La CLÉ de l'erreur, pas son texte : un changement de langue la
  // retraduit. Le serveur renvoie une phrase déjà faite, gardée en `text`.
  const [error, setError] = useState(null);
  const errorText = error && (error.key ? tForm(error.key) : error.text);

  const setField = useCallback((key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
  }, []);
  const pickHeadcount = useCallback((size) => setForm((f) => ({ ...f, headcount: size })), []);

  // Style des tranches d'effectif : celui de la référence au repos, et
  // l'état choisi de l'ancienne page (fond bleu pâle, bord et texte bleus).
  const headcountStyle = (size) => {
    const active = form.headcount === size;
    return {
      padding: '10px 6px', borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600',
      letterSpacing: '-0.005em', cursor: 'pointer', transition: '0.15s',
      background: active ? '#EFF6FF' : 'rgb(255, 255, 255)',
      border: `1px solid ${active ? '#1A56DB' : '#ECECEA'}`,
      color: active ? '#1A56DB' : 'rgb(75, 85, 99)',
    };
  };

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    // Validation côté page pour répondre tout de suite ; le serveur
    // revalide tout.
    if (!form.company.trim() || !form.name.trim() || !form.email.trim()) {
      setError({ key: 'errors.required' }); setStatus('error'); return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setError({ key: 'errors.email' }); setStatus('error'); return;
    }
    if (!form.headcount) {
      setError({ key: 'errors.headcount' }); setStatus('error'); return;
    }
    setStatus('loading');
    setError(null);
    const attribution = getAttribution() || {};
    try {
      await axios.post(`${API}/b2b/leads`, {
        company: form.company.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        headcount: form.headcount,
        message: form.message.trim() || null,
        website: form.website,           // anti-robot : vide chez un humain
        language: String(language || 'fr').slice(0, 2),
        utm_source: attribution.utm_source || null,
        utm_medium: attribution.utm_medium || null,
        utm_campaign: attribution.utm_campaign || null,
        ref: attribution.ref || null,
        referrer: attribution.referrer || null,
        landing_page: attribution.landing_page || null,
      });
      setStatus('sent');
    } catch (err) {
      // Rien n'est vidé : la saisie reste en place pour un nouvel essai.
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' && detail ? { text: detail } : { key: 'errors.generic' });
      setStatus('error');
    }
  }, [form, status, language]);

  return (
    <div className="teams-v3" data-teams-v3="">
      {metadata && <PageHead metadata={metadata} />}
      <PublicHeader authCta="dashboard" />
      <main>
        <div className="teams-page" data-testid="teams-page" style={{ "maxWidth": "100vw", "overflowX": "hidden", "fontFamily": "Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif", "background": "rgb(255, 255, 255)", "color": "rgb(10, 10, 15)" }}>
          <section className="tp-hero archer-hero-section" style={{ "padding": "72px 40px 24px", "background": "linear-gradient(180deg, #FFFFFF 0%, #EFF6FF 100%)", "position": "relative", "overflow": "hidden" }}>
            <div aria-hidden="true" style={{ "position": "absolute", "left": "50%", "top": "-140px", "width": "1100px", "height": "560px", "transform": "translateX(-50%)", "background": "radial-gradient(ellipse at 50% 50%, rgba(96,165,250,0.16) 0%, rgba(96,165,250,0) 62%)", "pointerEvents": "none" }} />
            {"\n"}
            <div style={{ "position": "relative", "maxWidth": "1120px", "margin": "0px auto" }}>
              {"\n"}
              <div className="tp4-hero-grid" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 7fr) minmax(0, 5fr)", "gap": "48px", "alignItems": "start" }}>
                {"\n"}
                <div style={{ "paddingTop": "36px" }}>
                  {"\n"}
                  <div style={{ "display": "inline-flex", "alignItems": "center", "gap": "8px", "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.2em", "textTransform": "uppercase", "color": "#15803D", "background": "#F0FDF4", "border": "1px solid #BBF7D0", "borderRadius": "999px", "padding": "6px 16px", "marginBottom": "26px" }}>
                    <span style={{ "width": "7px", "height": "7px", "borderRadius": "50%", "background": "#16A34A" }} />
                    {t('hero.t01')}
                  </div>
                  {"\n"}
                  <h1 style={{ "margin": "0", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(40px, 4.6vw, 62px)", "fontWeight": "700", "letterSpacing": "-0.045em", "lineHeight": "1.02", "color": "#0A0A0F", "textWrap": "balance" }}>
                    {t('hero.t02')}
                    <br />
                    <span style={{ "color": "#1A56DB" }}>
                      {t('hero.t03')}
                    </span>
                  </h1>
                  {"\n"}
                  <p style={{ "margin": "22px 0 0", "maxWidth": "600px", "fontSize": "18px", "fontWeight": "400", "lineHeight": "1.7", "color": "#4B5563", "textWrap": "pretty" }}>
                    {t('hero.t04')}
                    <span style={{ "display": "inline-flex", "verticalAlign": "middle", "margin": "0 2px 0 4px", "color": "#16A34A" }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    {t('hero.t05')}
                    <span style={{ "display": "inline-flex", "verticalAlign": "middle", "margin": "0 2px 0 4px", "color": "#16A34A" }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    {t('hero.t06')}
                    <span style={{ "display": "inline-flex", "verticalAlign": "middle", "margin": "0 2px 0 4px", "color": "#16A34A" }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    {t('hero.t07')}
                    <span style={{ "fontWeight": "700", "color": "#0A0A0F" }}>
                      {t('hero.t08')}
                    </span>
                    <span style={{ "display": "inline-flex", "verticalAlign": "middle", "margin": "0 2px 0 4px", "color": "#16A34A" }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  </p>
                  {"\n"}
                  <div style={{ "display": "flex", "gap": "12px", "flexWrap": "wrap", "marginTop": "30px" }}>
                    <a className="tp-cta" href="#conseiller" data-testid="teams-hero-cta" style={{ "display": "inline-flex", "alignItems": "center", "gap": "9px", "background": "#1A56DB", "color": "#FFFFFF", "fontSize": "15.5px", "fontWeight": "600", "letterSpacing": "-0.01em", "padding": "14px 24px", "borderRadius": "12px", "boxShadow": "rgba(26, 86, 219, 0.2) 0px 4px 16px", "transition": "0.2s", "textDecoration": "none" }}>
                      {t('hero.t09')}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </a>
                    <a className="tp-cta-ghost" href="#fonctionnement" style={{ "display": "inline-flex", "alignItems": "center", "gap": "9px", "background": "#FFFFFF", "color": "#0A0A0F", "fontSize": "15.5px", "fontWeight": "600", "letterSpacing": "-0.01em", "padding": "14px 24px", "borderRadius": "12px", "border": "1px solid #ECECEA", "transition": "0.2s", "textDecoration": "none" }}>
                      {t('hero.t10')}
                    </a>
                  </div>
                  {"\n"}
                  <div className="tp5-faces" style={{ "marginTop": "36px", "display": "flex", "alignItems": "center", "justifyContent": "flex-start", "gap": "18px", "flexWrap": "wrap" }}>
                    {"\n"}
                    <div style={{ "display": "flex", "alignItems": "center" }}>
                      <span title={t('hero.t11')} style={{ "width": "54px", "height": "54px", "borderRadius": "50%", "overflow": "hidden", "border": "2.5px solid rgb(255, 255, 255)", "boxShadow": "0 2px 8px rgba(10,10,15,0.14)", "marginLeft": "0", "background": "rgb(244, 244, 241)", "display": "block" }}>
                        <FTSlot id="tp-hero-f1" shape="circle" data-to-fill="" src="/teams-v3/hero-visage-1.webp" hostStyle={{ "display": "block", "width": "49px", "height": "49px" }} />
                      </span>
                      <span title={t('hero.t12')} style={{ "width": "54px", "height": "54px", "borderRadius": "50%", "overflow": "hidden", "border": "2.5px solid rgb(255, 255, 255)", "boxShadow": "0 2px 8px rgba(10,10,15,0.14)", "marginLeft": "-16px", "background": "rgb(244, 244, 241)", "display": "block" }}>
                        <FTSlot id="tp-hero-f2" shape="circle" data-to-fill="" src="/teams-v3/hero-visage-2.webp" hostStyle={{ "display": "block", "width": "49px", "height": "49px" }} />
                      </span>
                      <span title={t('hero.t13')} style={{ "width": "54px", "height": "54px", "borderRadius": "50%", "overflow": "hidden", "border": "2.5px solid rgb(255, 255, 255)", "boxShadow": "0 2px 8px rgba(10,10,15,0.14)", "marginLeft": "-16px", "background": "rgb(244, 244, 241)", "display": "block" }}>
                        <FTSlot id="tp-hero-f3" shape="circle" data-to-fill="" src="/teams-v3/hero-visage-3.webp" hostStyle={{ "display": "block", "width": "49px", "height": "49px" }} />
                      </span>
                      <span title={t('hero.t14')} style={{ "width": "54px", "height": "54px", "borderRadius": "50%", "overflow": "hidden", "border": "2.5px solid rgb(255, 255, 255)", "boxShadow": "0 2px 8px rgba(10,10,15,0.14)", "marginLeft": "-16px", "background": "rgb(244, 244, 241)", "display": "block" }}>
                        <FTSlot id="tp-hero-f4" shape="circle" data-to-fill="" src="/teams-v3/hero-visage-4.webp" hostStyle={{ "display": "block", "width": "49px", "height": "49px" }} />
                      </span>
                    </div>
                    {"\n"}
                    <span style={{ "fontSize": "16px", "fontWeight": "500", "letterSpacing": "-0.01em", "color": "rgb(55, 65, 81)" }}>
                      {t('hero.t15')}
                    </span>
                    {"\n"}
                  </div>
                  {"\n"}
                </div>
                {"\n"}
                <div className="tp4-hero-visual" style={{ "position": "relative", "display": "flex", "justifyContent": "center", "padding": "36px 0 0" }}>
                  {"\n"}
                  <div aria-hidden="true" style={{ "position": "absolute", "left": "50%", "top": "50%", "transform": "translate(-50%, -50%)", "width": "640px", "height": "640px", "borderRadius": "50%", "background": "radial-gradient(circle at center, rgba(26,86,219,0.16) 0%, rgba(26,86,219,0) 64%)", "pointerEvents": "none" }} />
                  {"\n"}
                  <div className="tp5-phone" style={{ "position": "relative", "width": "290px", "height": "596px", "borderRadius": "46px", "background": "linear-gradient(160deg, #2A2A32 0%, #0A0A0F 60%)", "padding": "11px", "boxShadow": "0 40px 80px rgba(10,10,15,0.22), 0 0 0 1px rgba(255,255,255,0.08) inset, 0 2px 6px rgba(10,10,15,0.25)" }}>
                    {"\n"}
                    <div style={{ "width": "100%", "height": "100%", "borderRadius": "36px", "background": "#FFFFFF", "overflow": "hidden", "display": "flex", "flexDirection": "column", "position": "relative" }}>
                      {"\n"}
                      <div aria-hidden="true" style={{ "position": "absolute", "left": "50%", "top": "-120px", "transform": "translateX(-50%)", "width": "420px", "height": "320px", "background": "radial-gradient(ellipse at center, rgba(96,165,250,0.18) 0%, rgba(96,165,250,0) 62%)", "pointerEvents": "none" }} />
                      {"\n"}
                      <div style={{ "position": "relative", "padding": "14px 18px 0", "display": "flex", "alignItems": "center", "justifyContent": "space-between" }}>
                        <span style={{ "fontSize": "11px", "fontWeight": "600", "color": "#0A0A0F", "letterSpacing": "0.02em" }}>
                          {t('hero.t16')}
                        </span>
                        <span style={{ "width": "62px", "height": "18px", "borderRadius": "999px", "background": "#0A0A0F" }} />
                        <span style={{ "display": "inline-flex", "gap": "3px", "alignItems": "flex-end" }}>
                          <span style={{ "width": "3px", "height": "5px", "background": "#0A0A0F", "borderRadius": "1px" }} />
                          <span style={{ "width": "3px", "height": "7px", "background": "#0A0A0F", "borderRadius": "1px" }} />
                          <span style={{ "width": "3px", "height": "9px", "background": "#0A0A0F", "borderRadius": "1px" }} />
                        </span>
                      </div>
                      {"\n"}
                      <div style={{ "position": "relative", "padding": "14px 18px 0", "display": "flex", "alignItems": "center", "justifyContent": "space-between" }}>
                        <span style={{ "display": "inline-flex", "alignItems": "center", "gap": "4px", "fontSize": "11px", "fontWeight": "600", "color": "#5A5A65" }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="m15 18-6-6 6-6" />
                          </svg>
                          {t('hero.t17')}
                        </span>
                        <span style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "13px", "fontWeight": "800", "letterSpacing": "-0.04em", "color": "#1A56DB" }}>
                          {t('hero.t18')}
                        </span>
                      </div>
                      {"\n"}
                      <div style={{ "position": "relative", "padding": "18px 18px 0" }}>
                        {"\n"}
                        <div style={{ "display": "inline-flex", "alignItems": "center", "gap": "6px", "background": "#EFF6FF", "color": "#1A56DB", "borderRadius": "999px", "padding": "3px 9px", "fontSize": "9.5px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase" }}>
                          {t('hero.t19')}
                        </div>
                        {"\n"}
                        <div style={{ "marginTop": "10px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "20px", "fontWeight": "700", "letterSpacing": "-0.035em", "lineHeight": "1.12", "color": "#0A0A0F", "textWrap": "balance" }}>
                          {t('hero.t20')}
                        </div>
                        {"\n"}
                        <div style={{ "marginTop": "8px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "13.5px", "fontWeight": "600", "letterSpacing": "-0.02em", "lineHeight": "1.3", "color": "#1A56DB" }}>
                          {t('hero.t21')}
                        </div>
                        {"\n"}
                      </div>
                      {"\n"}
                      <div style={{ "position": "relative", "margin": "16px 14px 0" }}>
                        {"\n"}
                        <div style={{ "display": "flex", "alignItems": "center", "gap": "6px", "marginBottom": "8px", "paddingLeft": "2px" }}>
                          <span className="tp5-live" style={{ "width": "8px", "height": "8px", "borderRadius": "50%", "background": "#16A34A", "boxShadow": "0 0 0 2px rgba(22,163,74,0.2)" }} />
                          <span style={{ "fontSize": "9.5px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#15803D" }}>
                            {t('hero.t22')}
                          </span>
                        </div>
                        {"\n"}
                        <div style={{ "background": "#FFFFFF", "border": "1px solid #DBEAFE", "borderRadius": "16px", "padding": "12px", "boxShadow": "0 14px 32px rgba(26,86,219,0.12)" }}>
                          {"\n"}
                          <div style={{ "display": "flex", "gap": "9px", "alignItems": "flex-start" }}>
                            <span style={{ "flex": "0 0 auto", "width": "30px", "height": "30px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1" }}>
                              <FTSlot id="tp5-phone-lea" shape="circle" data-mini="" src="/teams-v3/juriste-lea.png" hostStyle={{ "display": "block", "width": "30px", "height": "30px" }} />
                            </span>
                            {"\n"}
                            <div style={{ "flex": "1 1 0%", "minWidth": "0" }}>
                              <div style={{ "background": "#F4F4F1", "borderRadius": "3px 12px 12px 12px", "padding": "9px 11px", "fontSize": "12px", "lineHeight": "1.5", "color": "#0A0A0F" }}>
                                {t('hero.t23')}
                              </div>
                              <div style={{ "marginTop": "5px", "paddingLeft": "3px", "fontSize": "10.5px", "fontWeight": "500", "color": "#8A8A95" }}>
                                {t('hero.t24')}
                              </div>
                            </div>
                          </div>
                          {"\n"}
                          <div style={{ "marginTop": "10px", "paddingTop": "10px", "borderTop": "1px solid #F1F1EC", "fontSize": "11.5px", "fontWeight": "700", "color": "#0A0A0F" }}>
                            {t('hero.t25')}
                          </div>
                          {"\n"}
                        </div>
                      </div>
                      {"\n"}
                      <div style={{ "position": "relative", "margin": "auto 14px 16px", "background": "#F7F7F5", "borderRadius": "14px", "padding": "12px 14px" }}>
                        <div style={{ "display": "flex", "alignItems": "center", "gap": "10px", "position": "relative", "padding": "0 0 12px" }}>
                          <span aria-hidden="true" style={{ "position": "absolute", "left": "7px", "top": "16px", "bottom": "-2px", "width": "2px", "background": "#1A56DB" }} />
                          <span style={{ "position": "relative", "width": "16px", "height": "16px", "borderRadius": "50%", "background": "#1A56DB", "color": "#fff", "display": "flex", "alignItems": "center", "justifyContent": "center", "flex": "0 0 auto" }}>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <span style={{ "fontSize": "11.5px", "fontWeight": "500", "color": "#5A5A65" }}>
                            {t('hero.t26')}
                          </span>
                        </div>
                        <div style={{ "display": "flex", "alignItems": "center", "gap": "10px", "position": "relative", "padding": "0 0 12px" }}>
                          <span aria-hidden="true" style={{ "position": "absolute", "left": "7px", "top": "16px", "bottom": "-2px", "width": "2px", "background": "#1A56DB" }} />
                          <span style={{ "position": "relative", "width": "16px", "height": "16px", "borderRadius": "50%", "background": "#1A56DB", "color": "#fff", "display": "flex", "alignItems": "center", "justifyContent": "center", "flex": "0 0 auto" }}>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <span style={{ "fontSize": "11.5px", "fontWeight": "500", "color": "#5A5A65" }}>
                            {t('hero.t27')}
                          </span>
                        </div>
                        <div style={{ "display": "flex", "alignItems": "center", "gap": "10px", "position": "relative", "padding": "0 0 12px" }}>
                          <span aria-hidden="true" style={{ "position": "absolute", "left": "7px", "top": "16px", "bottom": "-2px", "width": "2px", "background": "#E2E2DC" }} />
                          <span style={{ "position": "relative", "width": "16px", "height": "16px", "borderRadius": "50%", "background": "#1A56DB", "boxShadow": "0 0 0 3px rgba(26,86,219,0.16)", "flex": "0 0 auto" }} />
                          <span style={{ "fontSize": "11.5px", "fontWeight": "700", "color": "#0A0A0F" }}>
                            {t('hero.t28')}
                          </span>
                        </div>
                        <div style={{ "display": "flex", "alignItems": "center", "gap": "10px", "position": "relative", "padding": "0" }}>
                          <span style={{ "position": "relative", "width": "16px", "height": "16px", "borderRadius": "50%", "background": "#fff", "border": "2px solid #D6D6D0", "boxSizing": "border-box", "flex": "0 0 auto" }} />
                          <span style={{ "fontSize": "11.5px", "fontWeight": "500", "color": "#8A8A95" }}>
                            {t('hero.t29')}
                          </span>
                        </div>
                      </div>
                      {"\n"}
                    </div>
                  </div>
                  {"\n"}
                  <div className="tp5-float2" style={{ "position": "absolute", "bottom": "118px", "left": "50%", "transform": "translateX(calc(-50% - 218px))", "zIndex": "2", "display": "flex", "alignItems": "center", "gap": "10px", "background": "rgba(255,255,255,0.96)", "backdropFilter": "blur(12px)", "border": "1px solid #DBEAFE", "borderRadius": "16px", "padding": "10px 14px", "boxShadow": "0 12px 32px rgba(26,86,219,0.14)", "whiteSpace": "nowrap" }}>
                    {"\n"}
                    <span style={{ "flex": "0 0 auto", "width": "32px", "height": "32px", "borderRadius": "9px", "background": "#EFF6FF", "color": "#1A56DB", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <polyline points="12 7 12 12 15 14" />
                      </svg>
                    </span>
                    {"\n"}
                    <span style={{ "display": "flex", "flexDirection": "column", "gap": "1px" }}>
                      <span style={{ "fontSize": "10.5px", "fontWeight": "700", "letterSpacing": "0.06em", "textTransform": "uppercase", "color": "#8A8A95" }}>
                        {t('hero.t30')}
                      </span>
                      <span style={{ "fontSize": "13px", "fontWeight": "700", "letterSpacing": "-0.01em", "color": "#0A0A0F" }}>
                        {t('hero.t31')}
                      </span>
                    </span>
                    {"\n"}
                  </div>
                  {"\n"}
                  <div className="tp5-notif" style={{ "position": "absolute", "top": "58px", "left": "50%", "transform": "translateX(calc(-50% + 118px))", "zIndex": "2", "display": "flex", "alignItems": "center", "gap": "10px", "background": "rgba(255,255,255,0.96)", "backdropFilter": "blur(12px)", "border": "1px solid #ECECEA", "borderRadius": "18px", "padding": "10px 14px 10px 10px", "boxShadow": "0 12px 32px rgba(10,10,15,0.12)", "whiteSpace": "nowrap" }}>
                    {"\n"}
                    <span style={{ "flex": "0 0 auto", "width": "34px", "height": "34px", "borderRadius": "9px", "background": "#1A56DB", "color": "#fff", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "17px", "fontWeight": "800", "letterSpacing": "-0.04em" }}>
                      {t('hero.t32')}
                    </span>
                    {"\n"}
                    <span style={{ "display": "flex", "flexDirection": "column", "gap": "1px" }}>
                      <span style={{ "display": "flex", "justifyContent": "space-between", "gap": "14px", "fontSize": "11px", "fontWeight": "600", "letterSpacing": "0.02em", "textTransform": "uppercase", "color": "#8A8A95" }}>
                        <span>
                          {t('hero.t33')}
                        </span>
                        <span>
                          {t('hero.t34')}
                        </span>
                      </span>
                      <span style={{ "fontSize": "13px", "fontWeight": "600", "letterSpacing": "-0.01em", "color": "#0A0A0F" }}>
                        {t('hero.t35')}
                      </span>
                    </span>
                    {"\n"}
                  </div>
                </div>
                {"\n\n"}
              </div>
            </div>
          </section>
          <section id="probleme" className="tp-sec" style={{ "padding": "48px 40px", "background": "#FFFFFF", "borderTop": "1px solid #ECECEA" }}>
            <div style={{ "maxWidth": "1120px", "margin": "0px auto" }}>
              {"\n"}
              <div style={{ "marginBottom": "44px" }}>
                <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.2em", "textTransform": "uppercase", "color": "#1A56DB", "marginBottom": "14px" }}>
                  {t('probleme.t01')}
                </div>
                <h2 style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(30px, 3.4vw, 42px)", "fontWeight": "700", "letterSpacing": "-0.034em", "lineHeight": "1.08", "margin": "0px", "maxWidth": "820px", "color": "#0A0A0F", "textWrap": "balance" }}>
                  {t('probleme.t02')}
                  <span style={{ "color": "#1A56DB" }}>
                    {t('probleme.t03')}
                  </span>
                </h2>
              </div>
              {"\n"}
              <div className="tp-grid3" style={{ "display": "grid", "gridTemplateColumns": "repeat(3, minmax(0, 1fr))", "gap": "14px", "alignItems": "stretch" }}>
                {"\n"}
                <div className="tp4-card" style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "30px 28px 30px", "display": "flex", "flexDirection": "column", "gap": "14px", "transition": "0.25s" }}>
                  <span aria-hidden="true" style={{ "width": "36px", "height": "3px", "borderRadius": "3px", "background": "#1A56DB" }} />
                  <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(48px, 4.6vw, 64px)", "fontWeight": "700", "letterSpacing": "-0.05em", "lineHeight": "1", "color": "#1A56DB", "whiteSpace": "nowrap", "fontVariantNumeric": "tabular-nums" }}>
                    {t('probleme.t04')}
                  </div>
                  <div style={{ "fontSize": "16px", "fontWeight": "500", "lineHeight": "1.5", "color": "#4B5563", "textWrap": "pretty" }}>
                    {t('probleme.t05')}
                  </div>
                </div>
                {"\n"}
                <div className="tp4-card" style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "30px 28px 30px", "display": "flex", "flexDirection": "column", "gap": "14px", "transition": "0.25s" }}>
                  <span aria-hidden="true" style={{ "width": "36px", "height": "3px", "borderRadius": "3px", "background": "#1A56DB" }} />
                  <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(48px, 4.6vw, 64px)", "fontWeight": "700", "letterSpacing": "-0.05em", "lineHeight": "1", "color": "#1A56DB", "whiteSpace": "nowrap", "fontVariantNumeric": "tabular-nums" }}>
                    {t('probleme.t06')}
                  </div>
                  <div style={{ "fontSize": "16px", "fontWeight": "500", "lineHeight": "1.5", "color": "#4B5563", "textWrap": "pretty" }}>
                    {t('probleme.t07')}
                  </div>
                </div>
                {"\n"}
                <div className="tp4-card" style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "30px 28px 30px", "display": "flex", "flexDirection": "column", "gap": "14px", "transition": "0.25s" }}>
                  <span aria-hidden="true" style={{ "width": "36px", "height": "3px", "borderRadius": "3px", "background": "#1A56DB" }} />
                  <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(48px, 4.6vw, 64px)", "fontWeight": "700", "letterSpacing": "-0.05em", "lineHeight": "1", "color": "#1A56DB", "whiteSpace": "nowrap", "fontVariantNumeric": "tabular-nums" }}>
                    {t('probleme.t08')}
                  </div>
                  <div style={{ "fontSize": "16px", "fontWeight": "500", "lineHeight": "1.5", "color": "#4B5563", "textWrap": "pretty" }}>
                    {t('probleme.t09')}
                  </div>
                </div>
                {"\n"}
              </div>
              {"\n\n"}
              <div className="tp5-band" style={{ "marginTop": "44px", "display": "grid", "gridTemplateColumns": "56px minmax(0, 1fr) auto", "gap": "24px", "alignItems": "center", "background": "linear-gradient(135deg, #1A56DB 0%, #1E40AF 100%)", "borderRadius": "20px", "padding": "32px 36px", "boxShadow": "0 20px 48px rgba(26,86,219,0.22)" }}>
                {"\n"}
                <span style={{ "width": "56px", "height": "56px", "borderRadius": "16px", "background": "rgba(255,255,255,0.14)", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "28px", "lineHeight": "1" }}>
                  {t('probleme.t10')}
                </span>
                {"\n"}
                <div>
                  <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(22px, 2.4vw, 30px)", "fontWeight": "700", "letterSpacing": "-0.03em", "lineHeight": "1.15", "color": "#FFFFFF" }}>
                    {t('probleme.t11')}
                  </div>
                  {"\n"}
                  <p style={{ "margin": "8px 0 0", "maxWidth": "720px", "fontSize": "15.5px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgba(255,255,255,0.85)", "textAlign": "justify" }}>
                    {t('probleme.t12')}
                  </p>
                </div>
                {"\n"}
                <div className="tp5-band-stat" style={{ "textAlign": "center", "padding": "14px 22px", "background": "rgba(255,255,255,0.12)", "borderRadius": "14px", "border": "1px solid rgba(255,255,255,0.22)" }}>
                  <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "34px", "fontWeight": "800", "letterSpacing": "-0.04em", "lineHeight": "1", "color": "#FFFFFF" }}>
                    {t('probleme.t13')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "11px", "fontWeight": "700", "letterSpacing": "0.12em", "textTransform": "uppercase", "color": "rgba(255,255,255,0.8)" }}>
                    {t('probleme.t14')}
                  </div>
                </div>
                {"\n"}
              </div>
              {"\n"}
            </div>
          </section>
          <section id="gains" className="tp-sec" style={{ "padding": "48px 40px", "background": "#FFFFFF", "borderTop": "1px solid #ECECEA" }}>
            <div style={{ "maxWidth": "1120px", "margin": "0px auto" }}>
              {"\n"}
              <div style={{ "marginBottom": "36px" }}>
                <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.2em", "textTransform": "uppercase", "color": "#1A56DB", "marginBottom": "14px" }}>
                  {t('gains.t01')}
                </div>
                <h2 style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(30px, 3.4vw, 42px)", "fontWeight": "700", "letterSpacing": "-0.034em", "lineHeight": "1.08", "margin": "0px", "maxWidth": "760px", "color": "#0A0A0F", "textWrap": "balance" }}>
                  {t('gains.t02')}
                </h2>
              </div>
              {"\n"}
              <div className="tp-grid4" style={{ "display": "grid", "gridTemplateColumns": "repeat(4, minmax(0, 1fr))", "gap": "14px" }}>
                {"\n"}
                <div className="tp4-card" style={{ "display": "flex", "flexDirection": "column", "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "26px 24px 28px", "transition": "0.25s" }}>
                  {"\n"}
                  <div style={{ "display": "flex", "alignItems": "center", "gap": "12px" }}>
                    <span style={{ "flex": "0 0 auto", "width": "40px", "height": "40px", "borderRadius": "11px", "background": "#EFF6FF", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "22px", "lineHeight": "1" }}>
                      {t('gains.t03')}
                    </span>
                    <span style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.12em", "textTransform": "uppercase", "color": "#6B7280" }}>
                      {t('gains.t04')}
                    </span>
                  </div>
                  {"\n"}
                  <div style={{ "marginTop": "18px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "17px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.25", "color": "#0A0A0F", "whiteSpace": "nowrap", "overflow": "hidden", "textOverflow": "ellipsis" }}>
                    {t('gains.t05')}
                  </div>
                  {"\n"}
                  <div style={{ "marginTop": "10px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.6", "color": "#4B5563", "textAlign": "justify" }}>
                    {t('gains.t06')}
                  </div>
                  {"\n"}
                </div>
                {"\n"}
                <div className="tp4-card" style={{ "display": "flex", "flexDirection": "column", "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "26px 24px 28px", "transition": "0.25s" }}>
                  {"\n"}
                  <div style={{ "display": "flex", "alignItems": "center", "gap": "12px" }}>
                    <span style={{ "flex": "0 0 auto", "width": "40px", "height": "40px", "borderRadius": "11px", "background": "#EFF6FF", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "22px", "lineHeight": "1" }}>
                      {t('gains.t07')}
                    </span>
                    <span style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.12em", "textTransform": "uppercase", "color": "#6B7280" }}>
                      {t('gains.t08')}
                    </span>
                  </div>
                  {"\n"}
                  <div style={{ "marginTop": "18px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "17px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.25", "color": "#0A0A0F", "whiteSpace": "nowrap", "overflow": "hidden", "textOverflow": "ellipsis" }}>
                    {t('gains.t09')}
                  </div>
                  {"\n"}
                  <div style={{ "marginTop": "10px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.6", "color": "#4B5563", "textAlign": "justify" }}>
                    {t('gains.t10')}
                  </div>
                  {"\n"}
                </div>
                {"\n"}
                <div className="tp4-card" style={{ "display": "flex", "flexDirection": "column", "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "26px 24px 28px", "transition": "0.25s" }}>
                  {"\n"}
                  <div style={{ "display": "flex", "alignItems": "center", "gap": "12px" }}>
                    <span style={{ "flex": "0 0 auto", "width": "40px", "height": "40px", "borderRadius": "11px", "background": "#EFF6FF", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "22px", "lineHeight": "1" }}>
                      {t('gains.t11')}
                    </span>
                    <span style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.12em", "textTransform": "uppercase", "color": "#6B7280" }}>
                      {t('gains.t12')}
                    </span>
                  </div>
                  {"\n"}
                  <div style={{ "marginTop": "18px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "17px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.25", "color": "#0A0A0F", "whiteSpace": "nowrap", "overflow": "hidden", "textOverflow": "ellipsis" }}>
                    {t('gains.t13')}
                  </div>
                  {"\n"}
                  <div style={{ "marginTop": "10px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.6", "color": "#4B5563", "textAlign": "justify" }}>
                    {t('gains.t14')}
                  </div>
                  {"\n"}
                </div>
                {"\n"}
                <div className="tp4-card" style={{ "display": "flex", "flexDirection": "column", "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "26px 24px 28px", "transition": "0.25s" }}>
                  {"\n"}
                  <div style={{ "display": "flex", "alignItems": "center", "gap": "12px" }}>
                    <span style={{ "flex": "0 0 auto", "width": "40px", "height": "40px", "borderRadius": "11px", "background": "#EFF6FF", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "22px", "lineHeight": "1" }}>
                      {t('gains.t15')}
                    </span>
                    <span style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.12em", "textTransform": "uppercase", "color": "#6B7280" }}>
                      {t('gains.t16')}
                    </span>
                  </div>
                  {"\n"}
                  <div style={{ "marginTop": "18px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "17px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.25", "color": "#0A0A0F", "whiteSpace": "nowrap", "overflow": "hidden", "textOverflow": "ellipsis" }}>
                    {t('gains.t17')}
                  </div>
                  {"\n"}
                  <div style={{ "marginTop": "10px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.6", "color": "#4B5563", "textAlign": "justify" }}>
                    {t('gains.t18')}
                  </div>
                  {"\n"}
                </div>
                {"\n"}
              </div>
              <div style={{ "marginTop": "32px", "background": "#EFF6FF", "borderRadius": "16px", "padding": "44px 48px", "display": "flex", "flexDirection": "column", "alignItems": "center", "textAlign": "center", "gap": "14px" }}>
                {"\n"}
                <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.2em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                  {t('gains.t19')}
                </div>
                {"\n"}
                <div style={{ "maxWidth": "880px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(24px, 2.8vw, 36px)", "fontWeight": "700", "letterSpacing": "-0.034em", "lineHeight": "1.18", "color": "#0A0A0F", "textWrap": "balance" }}>
                  {t('gains.t20')}
                </div>
                {"\n"}
              </div>
            </div>
          </section>
          <section id="fonctionnement" className="tp-sec" style={{ "padding": "48px 40px", "background": "#FFFFFF", "borderTop": "1px solid #ECECEA", "scrollMarginTop": "64px" }}>
            <div style={{ "maxWidth": "1120px", "margin": "0px auto" }}>
              {"\n"}
              <div style={{ "marginBottom": "36px" }}>
                <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.2em", "textTransform": "uppercase", "color": "#1A56DB", "marginBottom": "14px" }}>
                  {t('fonctionnement.t01')}
                </div>
                <h2 style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(30px, 3.4vw, 42px)", "fontWeight": "700", "letterSpacing": "-0.034em", "lineHeight": "1.08", "margin": "0px", "maxWidth": "760px", "color": "#0A0A0F", "textWrap": "balance" }}>
                  {t('fonctionnement.t02')}
                </h2>
              </div>
              {"\n"}
              <div className="tp-grid3" style={{ "display": "grid", "gridTemplateColumns": "repeat(3, minmax(0, 1fr))", "gap": "14px", "marginBottom": "36px" }}>
                {"\n"}
                <div className="tp4-card" style={{ "display": "flex", "alignItems": "flex-start", "gap": "14px", "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "20px 22px", "transition": "0.25s" }}>
                  <span style={{ "flex": "0 0 auto", "width": "44px", "height": "44px", "borderRadius": "12px", "background": "#EFF6FF", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "22px", "lineHeight": "1" }}>
                    {t('fonctionnement.t03')}
                  </span>
                  <div>
                    <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "19px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.2", "color": "#0A0A0F" }}>
                      {t('fonctionnement.t04')}
                    </div>
                    <div style={{ "marginTop": "5px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "#4B5563", "textWrap": "pretty" }}>
                      {t('fonctionnement.t05')}
                    </div>
                  </div>
                </div>
                {"\n"}
                <div className="tp4-card" style={{ "display": "flex", "alignItems": "flex-start", "gap": "14px", "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "20px 22px", "transition": "0.25s" }}>
                  <span style={{ "flex": "0 0 auto", "width": "44px", "height": "44px", "borderRadius": "12px", "background": "#EFF6FF", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "22px", "lineHeight": "1" }}>
                    {t('fonctionnement.t06')}
                  </span>
                  <div>
                    <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "19px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.2", "color": "#0A0A0F" }}>
                      {t('fonctionnement.t07')}
                    </div>
                    <div style={{ "marginTop": "5px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "#4B5563", "textWrap": "pretty" }}>
                      {t('fonctionnement.t08')}
                    </div>
                  </div>
                </div>
                {"\n"}
                <div className="tp4-card" style={{ "display": "flex", "alignItems": "flex-start", "gap": "14px", "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "20px 22px", "transition": "0.25s" }}>
                  <span style={{ "flex": "0 0 auto", "width": "44px", "height": "44px", "borderRadius": "12px", "background": "#EFF6FF", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "22px", "lineHeight": "1" }}>
                    {t('fonctionnement.t09')}
                  </span>
                  <div>
                    <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "19px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.2", "color": "#0A0A0F" }}>
                      {t('fonctionnement.t10')}
                    </div>
                    <div style={{ "marginTop": "5px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "#4B5563", "textWrap": "pretty" }}>
                      {t('fonctionnement.t11')}
                    </div>
                    <div style={{ "marginTop": "8px" }}>
                      <span style={{ "display": "inline-flex", "alignItems": "center", "gap": "6px", "background": "#F0FDF4", "color": "#15803D", "border": "1px solid #BBF7D0", "borderRadius": "999px", "padding": "4px 10px", "fontSize": "12px", "fontWeight": "700", "letterSpacing": "-0.005em" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {t('fonctionnement.t12')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {"\n"}
            </div>
            {"\n"}
            <div style={{ "margin": "-4px 0 40px" }}>
              {"\n"}
              <div className="tp5-pod" style={{ "display": "grid", "gridTemplateColumns": "auto minmax(0, 1fr) auto", "gap": "28px", "alignItems": "center", "background": "#F7F7F5", "border": "1px solid #ECECEA", "borderRadius": "20px", "padding": "26px 30px" }}>
                {"\n"}
                <div style={{ "display": "flex", "alignItems": "center" }}>
                  <span style={{ "flex": "0 0 auto", "width": "56px", "height": "56px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1", "boxShadow": "0 0 0 3px #fff, 0 6px 16px rgba(10,10,15,0.12)", "marginLeft": "0px", "position": "relative", "zIndex": "3", "willChange": "transform" }}>
                    <FTSlot id="tp-pod-1" shape="circle" data-mini="" src="/teams-v3/juriste-lea.png" hostStyle={{ "display": "block", "width": "56px", "height": "56px" }} />
                  </span>
                  <span style={{ "flex": "0 0 auto", "width": "56px", "height": "56px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1", "boxShadow": "0 0 0 3px #fff, 0 6px 16px rgba(10,10,15,0.12)", "marginLeft": "-16px", "position": "relative", "zIndex": "2", "willChange": "transform" }}>
                    <FTSlot id="tp-pod-2" shape="circle" data-mini="" src="/teams-v3/podcast-juriste-2.png" hostStyle={{ "display": "block", "width": "56px", "height": "56px" }} />
                  </span>
                  <span style={{ "flex": "0 0 auto", "width": "56px", "height": "56px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1", "boxShadow": "0 0 0 3px #fff, 0 6px 16px rgba(10,10,15,0.12)", "marginLeft": "-16px", "position": "relative", "zIndex": "1" }}>
                    <FTSlot id="tp-pod-3" shape="circle" data-mini="" src="/teams-v3/podcast-juriste-3.png" hostStyle={{ "display": "block", "width": "56px", "height": "56px" }} />
                  </span>
                </div>
                {"\n"}
                <div style={{ "minWidth": "0" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.18em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                    {t('fonctionnement.t13')}
                  </div>
                  <div style={{ "marginTop": "6px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "22px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.2", "color": "#0A0A0F" }}>
                    {t('fonctionnement.t14')}
                  </div>
                  <p style={{ "margin": "6px 0 0", "fontSize": "15px", "fontWeight": "400", "lineHeight": "1.55", "color": "#4B5563", "textWrap": "pretty" }}>
                    {t('fonctionnement.t15')}
                  </p>
                </div>
                {"\n"}
                <a href="#" className="tp-cta" style={{ "display": "inline-flex", "alignItems": "center", "gap": "10px", "background": "#1A56DB", "color": "#FFFFFF", "fontSize": "15px", "fontWeight": "600", "letterSpacing": "-0.01em", "padding": "13px 20px 13px 14px", "borderRadius": "999px", "boxShadow": "rgba(26, 86, 219, 0.2) 0px 4px 16px", "textDecoration": "none", "whiteSpace": "nowrap", "transition": "0.2s" }} onClick={inert}>
                  <span style={{ "width": "30px", "height": "30px", "borderRadius": "50%", "background": "rgba(255,255,255,0.18)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
                      <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" />
                    </svg>
                  </span>
                  {t('fonctionnement.t16')}
                </a>
                {"\n"}
              </div>
              {"\n"}
            </div>
            {"\n"}
            <div style={{ "marginBottom": "14px", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "22px", "fontWeight": "700", "letterSpacing": "-0.028em", "color": "#0A0A0F" }}>
              {t('fonctionnement.t17')}
            </div>
            {"\n"}
            <div className="tp5-cmp-mobile" style={{ "display": "none", "flexDirection": "column", "gap": "12px" }}>
              <div style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "4px 18px 6px" }}>
                <div style={{ "padding": "14px 0 6px", "fontSize": "12px", "fontWeight": "800", "letterSpacing": "0.14em", "textTransform": "uppercase", "color": "#6B7280" }}>
                  {t('fonctionnement.t18')}
                </div>
                <div style={{ "padding": "12px 0", "borderTop": "1px solid #ECECEA" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#8A8A95" }}>
                    {t('fonctionnement.t19')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.5", "color": "#4B5563" }}>
                    {t('fonctionnement.t20')}
                  </div>
                </div>
                <div style={{ "padding": "12px 0", "borderTop": "1px solid #ECECEA" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#8A8A95" }}>
                    {t('fonctionnement.t21')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.5", "color": "#4B5563" }}>
                    {t('fonctionnement.t22')}
                  </div>
                </div>
                <div style={{ "padding": "12px 0", "borderTop": "1px solid #ECECEA" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#8A8A95" }}>
                    {t('fonctionnement.t23')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.5", "color": "#4B5563" }}>
                    {t('fonctionnement.t24')}
                  </div>
                </div>
                <div style={{ "padding": "12px 0", "borderTop": "1px solid #ECECEA" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#8A8A95" }}>
                    {t('fonctionnement.t25')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.5", "color": "#4B5563" }}>
                    {t('fonctionnement.t26')}
                  </div>
                </div>
              </div>
              <div style={{ "background": "#EFF6FF", "border": "1px solid #BFDBFE", "borderRadius": "16px", "padding": "4px 18px 6px" }}>
                <div style={{ "padding": "14px 0 6px", "fontSize": "12px", "fontWeight": "800", "letterSpacing": "0.14em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                  {t('fonctionnement.t27')}
                </div>
                <div style={{ "padding": "12px 0", "borderTop": "1px solid #DBEAFE" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                    {t('fonctionnement.t28')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "14px", "fontWeight": "500", "lineHeight": "1.5", "color": "#0A0A0F" }}>
                    {t('fonctionnement.t29')}
                  </div>
                </div>
                <div style={{ "padding": "12px 0", "borderTop": "1px solid #DBEAFE" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                    {t('fonctionnement.t30')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "14px", "fontWeight": "500", "lineHeight": "1.5", "color": "#0A0A0F" }}>
                    {t('fonctionnement.t31')}
                  </div>
                </div>
                <div style={{ "padding": "12px 0", "borderTop": "1px solid #DBEAFE" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                    {t('fonctionnement.t32')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "14px", "fontWeight": "500", "lineHeight": "1.5", "color": "#0A0A0F" }}>
                    {t('fonctionnement.t33')}
                  </div>
                </div>
                <div style={{ "padding": "12px 0", "borderTop": "1px solid #DBEAFE" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.1em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                    {t('fonctionnement.t34')}
                  </div>
                  <div style={{ "marginTop": "4px", "fontSize": "14px", "fontWeight": "500", "lineHeight": "1.5", "color": "#0A0A0F" }}>
                    {t('fonctionnement.t35')}
                  </div>
                </div>
              </div>
            </div>
            {"\n"}
            <div className="tp-cmp-wrap" style={{ "border": "1px solid #ECECEA", "borderRadius": "16px", "overflow": "hidden", "background": "rgb(255, 255, 255)" }}>
              {"\n"}
              <div className="tp-cmp-row tp-cmp-head" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr)", "borderBottom": "1px solid #ECECEA" }}>
                {"\n"}
                <div style={{ "padding": "16px 24px" }} />
                {"\n"}
                <div style={{ "padding": "16px 24px", "display": "flex", "alignItems": "center" }}>
                  <span style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.14em", "textTransform": "uppercase", "color": "rgb(107, 114, 128)" }}>
                    {t('fonctionnement.t36')}
                  </span>
                </div>
                {"\n"}
                <div className="tp-cmp-avec" style={{ "padding": "16px 24px", "background": "rgb(239, 246, 255)", "display": "flex", "alignItems": "center" }}>
                  <span style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.14em", "textTransform": "uppercase", "color": "rgb(26, 86, 219)" }}>
                    {t('fonctionnement.t37')}
                  </span>
                </div>
                {"\n"}
              </div>
              {"\n"}
              <div className="tp-cmp-row" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr)" }}>
                {"\n"}
                <div style={{ "padding": "22px 24px", "fontSize": "15px", "fontWeight": "700", "letterSpacing": "-0.02em", "lineHeight": "1.35", "color": "rgb(10, 10, 15)" }}>
                  {t('fonctionnement.t38')}
                </div>
                {"\n"}
                <div style={{ "padding": "22px 24px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(107, 114, 128)", "textWrap": "pretty" }}>
                  {t('fonctionnement.t39')}
                </div>
                {"\n"}
                <div className="tp-cmp-avec" style={{ "padding": "22px 24px", "background": "rgb(239, 246, 255)", "fontSize": "14px", "fontWeight": "500", "lineHeight": "1.6", "color": "rgb(10, 10, 15)", "textWrap": "pretty" }}>
                  {t('fonctionnement.t40')}
                </div>
                {"\n"}
              </div>
              {"\n"}
              <div className="tp-cmp-row" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr)", "borderTop": "1px solid #ECECEA" }}>
                {"\n"}
                <div style={{ "padding": "22px 24px", "fontSize": "15px", "fontWeight": "700", "letterSpacing": "-0.02em", "lineHeight": "1.35", "color": "rgb(10, 10, 15)" }}>
                  {t('fonctionnement.t41')}
                </div>
                {"\n"}
                <div style={{ "padding": "22px 24px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(107, 114, 128)", "textWrap": "pretty" }}>
                  {t('fonctionnement.t42')}
                </div>
                {"\n"}
                <div className="tp-cmp-avec" style={{ "padding": "22px 24px", "background": "rgb(239, 246, 255)", "fontSize": "14px", "fontWeight": "500", "lineHeight": "1.6", "color": "rgb(10, 10, 15)", "textWrap": "pretty" }}>
                  {t('fonctionnement.t43')}
                </div>
                {"\n"}
              </div>
              {"\n"}
              <div className="tp-cmp-row" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr)", "borderTop": "1px solid #ECECEA" }}>
                {"\n"}
                <div style={{ "padding": "22px 24px", "fontSize": "15px", "fontWeight": "700", "letterSpacing": "-0.02em", "lineHeight": "1.35", "color": "rgb(10, 10, 15)" }}>
                  {t('fonctionnement.t44')}
                </div>
                {"\n"}
                <div style={{ "padding": "22px 24px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(107, 114, 128)", "textWrap": "pretty" }}>
                  {t('fonctionnement.t45')}
                </div>
                {"\n"}
                <div className="tp-cmp-avec" style={{ "padding": "22px 24px", "background": "rgb(239, 246, 255)", "fontSize": "14px", "fontWeight": "500", "lineHeight": "1.6", "color": "rgb(10, 10, 15)", "textWrap": "pretty" }}>
                  {t('fonctionnement.t46')}
                </div>
                {"\n"}
              </div>
              {"\n"}
              <div className="tp-cmp-row" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr)", "borderTop": "1px solid #ECECEA" }}>
                {"\n"}
                <div style={{ "padding": "22px 24px", "fontSize": "15px", "fontWeight": "700", "letterSpacing": "-0.02em", "lineHeight": "1.35", "color": "rgb(10, 10, 15)" }}>
                  {t('fonctionnement.t47')}
                </div>
                {"\n"}
                <div style={{ "padding": "22px 24px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(107, 114, 128)", "textWrap": "pretty" }}>
                  {t('fonctionnement.t48')}
                </div>
                {"\n"}
                <div className="tp-cmp-avec" style={{ "padding": "22px 24px", "background": "rgb(239, 246, 255)", "fontSize": "14px", "fontWeight": "500", "lineHeight": "1.6", "color": "rgb(10, 10, 15)", "textWrap": "pretty" }}>
                  {t('fonctionnement.t49')}
                </div>
                {"\n"}
              </div>
              {"\n"}
            </div>
            {"\n"}
            <p style={{ "margin": "36px 0 0", "textAlign": "center", "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(22px, 2.4vw, 30px)", "fontWeight": "700", "letterSpacing": "-0.03em", "lineHeight": "1.2", "color": "#0A0A0F" }}>
              {t('fonctionnement.t50')}
              <span style={{ "color": "#1A56DB" }}>
                {t('fonctionnement.t51')}
              </span>
            </p>
          </section>
        </div>
        <section id="rh" className="tp-sec" style={{ "padding": "48px 40px", "background": "#FFFFFF", "borderTop": "1px solid #ECECEA" }}>
          <div style={{ "maxWidth": "1120px", "margin": "0px auto" }}>
            {"\n"}
            <div className="tp-teams-rh" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 1fr)", "gap": "56px", "alignItems": "center" }}>
              {"\n"}
              <div>
                <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.2em", "textTransform": "uppercase", "color": "#1A56DB", "marginBottom": "14px" }}>
                  {t('rh.t01')}
                </div>
                <h2 style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(30px, 3.4vw, 42px)", "fontWeight": "700", "letterSpacing": "-0.034em", "lineHeight": "1.08", "margin": "0px", "maxWidth": "760px", "color": "#0A0A0F", "textWrap": "balance" }}>
                  {t('rh.t02')}
                </h2>
                <p style={{ "margin": "18px 0px 0px", "maxWidth": "460px", "fontSize": "16px", "fontWeight": "400", "lineHeight": "1.65", "color": "#4B5563" }}>
                  {t('rh.t03')}
                </p>
              </div>
              {"\n"}
              <div style={{ "background": "rgb(255, 255, 255)", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "30px 30px 32px" }}>
                {"\n"}
                <div style={{ "display": "grid", "gridTemplateColumns": "36px minmax(0, 1fr)", "gap": "16px", "position": "relative", "paddingBottom": "26px" }}>
                  {"\n"}
                  <span aria-hidden="true" style={{ "position": "absolute", "left": "17px", "top": "40px", "bottom": "4px", "width": "2px", "borderRadius": "2px", "background": "rgb(219, 234, 254)", "overflow": "hidden" }}>
                    <span className="tp5-rhl" style={{ "position": "absolute", "left": "0", "right": "0", "top": "0", "height": "100%", "background": "rgb(26, 86, 219)", "transformOrigin": "top", "animationDelay": "0.5s" }} />
                  </span>
                  {"\n"}
                  <span className="tp5-rhn" style={{ "width": "36px", "height": "36px", "borderRadius": "50%", "background": "rgb(239, 246, 255)", "color": "rgb(26, 86, 219)", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "12.5px", "fontWeight": "800", "letterSpacing": "0.02em", "animationDelay": "0s" }}>
                    {t('rh.t04')}
                  </span>
                  {"\n"}
                  <div style={{ "paddingTop": "6px" }}>
                    <div style={{ "fontSize": "16.5px", "fontWeight": "700", "letterSpacing": "-0.02em", "lineHeight": "1.3", "color": "rgb(10, 10, 15)" }}>
                      {t('rh.t05')}
                    </div>
                    <div style={{ "marginTop": "6px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(107, 114, 128)" }}>
                      {t('rh.t06')}
                    </div>
                  </div>
                  {"\n"}
                </div>
                {"\n"}
                <div style={{ "display": "grid", "gridTemplateColumns": "36px minmax(0, 1fr)", "gap": "16px", "position": "relative", "paddingBottom": "26px" }}>
                  {"\n"}
                  <span aria-hidden="true" style={{ "position": "absolute", "left": "17px", "top": "40px", "bottom": "4px", "width": "2px", "borderRadius": "2px", "background": "rgb(219, 234, 254)", "overflow": "hidden" }}>
                    <span className="tp5-rhl" style={{ "position": "absolute", "left": "0", "right": "0", "top": "0", "height": "100%", "background": "rgb(26, 86, 219)", "transformOrigin": "top", "animationDelay": "1.9s" }} />
                  </span>
                  {"\n"}
                  <span className="tp5-rhn" style={{ "width": "36px", "height": "36px", "borderRadius": "50%", "background": "rgb(239, 246, 255)", "color": "rgb(26, 86, 219)", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "12.5px", "fontWeight": "800", "letterSpacing": "0.02em", "animationDelay": "1.4s" }}>
                    {t('rh.t07')}
                  </span>
                  {"\n"}
                  <div style={{ "paddingTop": "6px" }}>
                    <div style={{ "fontSize": "16.5px", "fontWeight": "700", "letterSpacing": "-0.02em", "lineHeight": "1.3", "color": "rgb(10, 10, 15)" }}>
                      {t('rh.t08')}
                    </div>
                    <div style={{ "marginTop": "6px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(107, 114, 128)" }}>
                      {t('rh.t09')}
                    </div>
                  </div>
                  {"\n"}
                </div>
                {"\n"}
                <div style={{ "display": "grid", "gridTemplateColumns": "36px minmax(0, 1fr)", "gap": "16px", "position": "relative", "paddingBottom": "0px" }}>
                  {"\n\n"}
                  <span className="tp5-rhn" style={{ "width": "36px", "height": "36px", "borderRadius": "50%", "background": "rgb(239, 246, 255)", "color": "rgb(26, 86, 219)", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "12.5px", "fontWeight": "800", "letterSpacing": "0.02em", "animationDelay": "2.8s" }}>
                    {t('rh.t10')}
                  </span>
                  {"\n"}
                  <div style={{ "paddingTop": "6px" }}>
                    <div style={{ "fontSize": "16.5px", "fontWeight": "700", "letterSpacing": "-0.02em", "lineHeight": "1.3", "color": "rgb(10, 10, 15)" }}>
                      {t('rh.t11')}
                    </div>
                    <div style={{ "marginTop": "6px", "fontSize": "14px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(107, 114, 128)" }}>
                      {t('rh.t12')}
                    </div>
                  </div>
                  {"\n"}
                </div>
                {"\n"}
              </div>
              {"\n"}
            </div>
            {"\n"}
            <div className="tp4-conf" style={{ "marginTop": "40px", "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "20px", "display": "grid", "gridTemplateColumns": "260px minmax(0, 1fr) minmax(0, 1.1fr)", "gap": "20px 28px", "alignItems": "center" }}>
              {"\n"}
              <div style={{ "position": "relative", "borderRadius": "16px", "overflow": "hidden", "background": "#E9E9E4", "border": "1px solid #ECECEA", "aspectRatio": "4 / 3", "borderRadius": "12px" }}>
                <FTSlot id="tp-equipe-photo" shape="rect" src="/teams-v3/equipe-archer.webp" hostStyle={{ "display": "block", "width": "100%", "height": "100%" }} />
              </div>
              {"\n"}
              <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(22px, 2.2vw, 27px)", "fontWeight": "700", "letterSpacing": "-0.03em", "lineHeight": "1.2", "color": "#0A0A0F", "textWrap": "balance" }}>
                {t('rh.t13')}
                <br />
                <span style={{ "color": "#1A56DB" }}>
                  {t('rh.t14')}
                </span>
              </div>
              {"\n"}
              <p style={{ "margin": "0", "fontSize": "15.5px", "fontWeight": "400", "lineHeight": "1.65", "color": "#4B5563", "textWrap": "pretty" }}>
                {t('rh.t15')}
              </p>
              {"\n"}
            </div>
          </div>
        </section>
        <section id="temoignage" className="tp-sec" style={{ "padding": "48px 40px", "background": "#FFFFFF", "borderTop": "1px solid #ECECEA" }}>
          <div style={{ "maxWidth": "1120px", "margin": "0px auto" }}>
            {"\n"}
            <div style={{ "marginBottom": "32px" }}>
              <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.2em", "textTransform": "uppercase", "color": "#1A56DB", "marginBottom": "14px" }}>
                {t('temoignage.t01')}
              </div>
              <h2 style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(30px, 3.4vw, 42px)", "fontWeight": "700", "letterSpacing": "-0.034em", "lineHeight": "1.08", "margin": "0px", "maxWidth": "760px", "color": "#0A0A0F", "textWrap": "balance" }}>
                {t('temoignage.t02')}
              </h2>
            </div>
            {"\n"}
            <div className="tp-tem-grid" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 1.45fr) minmax(0, 1fr)", "gap": "16px", "alignItems": "stretch" }}>
              {"\n"}
              <div className="tp-tem-card" style={{ "display": "grid", "gridTemplateColumns": "minmax(0, 0.9fr) minmax(0, 1.1fr)", "background": "rgb(255, 255, 255)", "border": "1px solid #ECECEA", "borderRadius": "16px", "overflow": "hidden" }}>
                {"\n"}
                <div style={{ "aspectRatio": "1 / 1", "background": "rgb(233, 233, 228)" }}>
                  <FTSlot id="tp-drh-photo" shape="rect" src="/teams-v3/drh-temoignage.webp" hostStyle={{ "display": "block", "width": "100%", "height": "100%" }} />
                </div>
                {"\n"}
                <div style={{ "padding": "30px 30px 28px", "display": "flex", "flexDirection": "column", "justifyContent": "space-between", "gap": "22px" }}>
                  {"\n"}
                  <div style={{ "fontSize": "clamp(17px, 1.6vw, 20px)", "fontWeight": "500", "letterSpacing": "-0.015em", "lineHeight": "1.5", "color": "rgb(42, 42, 53)", "textAlign": "justify" }}>
                    {t('temoignage.t03')}
                  </div>
                  {"\n"}
                  <div>
                    <div style={{ "fontSize": "15px", "fontWeight": "700", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)" }}>
                      {t('temoignage.t04')}
                    </div>
                    <div style={{ "marginTop": "3px", "fontSize": "13px", "fontWeight": "500", "color": "rgb(107, 114, 128)" }}>
                      {t('temoignage.t05')}
                    </div>
                  </div>
                  {"\n"}
                </div>
                {"\n"}
              </div>
              {"\n"}
              <div style={{ "position": "relative", "display": "flex", "flexDirection": "column", "justifyContent": "center" }}>
                {"\n"}
                <div style={{ "position": "relative", "aspectRatio": "16 / 9", "borderRadius": "16px", "overflow": "hidden", "background": "rgb(233, 233, 228)", "border": "1px solid #ECECEA" }}>
                  <FTSlot id="tp-video-cover" shape="rect" src="/teams-v3/video-couverture.webp" hostStyle={{ "display": "block", "width": "100%", "height": "100%" }} />
                  {"\n"}
                  <span style={{ "position": "absolute", "left": "50%", "top": "50%", "transform": "translate(-50%, -50%)", "width": "68px", "height": "68px", "borderRadius": "50%", "background": "rgb(255, 255, 255)", "display": "flex", "alignItems": "center", "justifyContent": "center", "color": "rgb(26, 86, 219)", "boxShadow": "rgba(10, 10, 15, 0.12) 0px 8px 24px", "pointerEvents": "none" }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
                      <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" />
                    </svg>
                  </span>
                  {"\n"}
                  <span style={{ "position": "absolute", "left": "16px", "bottom": "14px", "background": "rgb(255, 255, 255)", "borderRadius": "999px", "padding": "6px 12px", "fontSize": "12px", "fontWeight": "700", "color": "rgb(10, 10, 15)", "pointerEvents": "none" }}>
                    {t('temoignage.t06')}
                  </span>
                </div>
                {"\n"}
              </div>
              {"\n"}
            </div>
            {"\n"}
          </div>
        </section>
        <section id="conseiller" className="tp-sec" style={{ "padding": "48px 40px", "background": "#FFFFFF", "borderTop": "1px solid #ECECEA", "scrollMarginTop": "64px" }}>
          <div style={{ "maxWidth": "1120px", "margin": "0px auto" }}>
            {"\n"}
            <div style={{ "maxWidth": "720px", "margin": "0 auto", "textAlign": "center" }}>
              {"\n"}
              <div style={{ "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.2em", "textTransform": "uppercase", "color": "#1A56DB", "marginBottom": "14px" }}>
                {t('conseiller.t01')}
              </div>
              {"\n"}
              <h2 style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(30px, 3.4vw, 42px)", "fontWeight": "700", "letterSpacing": "-0.034em", "lineHeight": "1.08", "margin": "0px", "color": "#0A0A0F", "textWrap": "balance" }}>
                {t('conseiller.t02')}
              </h2>
              {"\n"}
              <div style={{ "marginTop": "22px", "display": "flex", "gap": "8px", "flexWrap": "wrap", "justifyContent": "center" }}>
                <span style={{ "display": "inline-flex", "alignItems": "center", "background": "#FFFFFF", "border": "1px solid #BFDBFE", "borderRadius": "999px", "padding": "7px 14px", "fontSize": "13px", "fontWeight": "600", "color": "#0A0A0F" }}>
                  {t('conseiller.t03')}
                </span>
                <span style={{ "display": "inline-flex", "alignItems": "center", "background": "#FFFFFF", "border": "1px solid #BFDBFE", "borderRadius": "999px", "padding": "7px 14px", "fontSize": "13px", "fontWeight": "600", "color": "#0A0A0F" }}>
                  {t('conseiller.t04')}
                </span>
                <span style={{ "display": "inline-flex", "alignItems": "center", "background": "#FFFFFF", "border": "1px solid #BFDBFE", "borderRadius": "999px", "padding": "7px 14px", "fontSize": "13px", "fontWeight": "600", "color": "#0A0A0F" }}>
                  {t('conseiller.t05')}
                </span>
              </div>
              {"\n\n"}
            </div>
            {"\n"}
            <div style={{ "maxWidth": "620px", "margin": "36px auto 0" }}>
              <form noValidate data-testid="teams-form" style={{ "background": "rgb(255, 255, 255)", "border": "1px solid #ECECEA", "borderRadius": "16px", "overflow": "hidden", "boxShadow": "rgba(10, 10, 15, 0.06) 0px 16px 40px" }} onSubmit={submit}>
                <div style={{ "padding": "24px 28px", "display": "flex", "alignItems": "center", "gap": "16px", "background": "linear-gradient(135deg, #1A56DB 0%, #1E40AF 100%)" }}>
                  {"\n"}
                  <span style={{ "flex": "0 0 auto", "width": "56px", "height": "56px", "borderRadius": "50%", "overflow": "hidden", "background": "rgba(255,255,255,0.18)", "boxShadow": "0 0 0 2px rgba(255,255,255,0.5)", "display": "block" }}>
                    <FTSlot id="tp-conseiller-photo" shape="circle" src="/teams-v3/conseiller.webp" hostStyle={{ "display": "block", "width": "56px", "height": "56px" }} />
                  </span>
                  {"\n"}
                  <div style={{ "flex": "1 1 0%", "minWidth": "0" }}>
                    <div style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "22px", "fontWeight": "700", "letterSpacing": "-0.028em", "lineHeight": "1.2", "color": "#FFFFFF" }}>
                      {t('conseiller.t06')}
                    </div>
                    <p style={{ "margin": "6px 0 0", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgba(255,255,255,0.85)" }}>
                      {t('conseiller.t07')}
                    </p>
                  </div>
                  {"\n"}
                </div>
                <div style={{ "padding": "28px", "display": "flex", "flexDirection": "column", "gap": "16px" }}>
                  {status === 'sent' ? (
                    <TeamsFormSent t={tForm} />
                  ) : (
                    <>
                      <Honeypot t={tForm} value={form.website} onChange={setField('website')} />
                      <div style={{ "display": "flex", "flexDirection": "column", "gap": "6px" }}>
                        <label htmlFor="tp-company" style={{ "fontSize": "12.5px", "fontWeight": "600", "letterSpacing": "-0.005em", "color": "rgb(55, 65, 81)" }}>
                          {t('conseiller.t08')}
                        </label>
                        <input id="tp-company" className="tp-input" placeholder={t('conseiller.t09')} autoComplete="organization" data-testid="teams-field-company" type="text" style={{ "width": "100%", "boxSizing": "border-box", "padding": "11px 14px", "border": "1px solid #ECECEA", "borderRadius": "10px", "background": "rgb(250, 249, 247)", "fontFamily": "inherit", "fontSize": "14px", "color": "rgb(10, 10, 15)", "outline": "none" }} value={form.company} onChange={setField('company')} />
                      </div>
                      <div className="tp-form-row" style={{ "display": "grid", "gridTemplateColumns": "1fr 1fr", "gap": "14px" }}>
                        <div style={{ "display": "flex", "flexDirection": "column", "gap": "6px" }}>
                          <label htmlFor="tp-name" style={{ "fontSize": "12.5px", "fontWeight": "600", "letterSpacing": "-0.005em", "color": "rgb(55, 65, 81)" }}>
                            {t('conseiller.t10')}
                          </label>
                          <input id="tp-name" className="tp-input" placeholder={t('conseiller.t11')} autoComplete="name" data-testid="teams-field-name" type="text" style={{ "width": "100%", "boxSizing": "border-box", "padding": "11px 14px", "border": "1px solid #ECECEA", "borderRadius": "10px", "background": "rgb(250, 249, 247)", "fontFamily": "inherit", "fontSize": "14px", "color": "rgb(10, 10, 15)", "outline": "none" }} value={form.name} onChange={setField('name')} />
                        </div>
                        <div style={{ "display": "flex", "flexDirection": "column", "gap": "6px" }}>
                          <label htmlFor="tp-email" style={{ "fontSize": "12.5px", "fontWeight": "600", "letterSpacing": "-0.005em", "color": "rgb(55, 65, 81)" }}>
                            {t('conseiller.t12')}
                          </label>
                          <input id="tp-email" className="tp-input" placeholder={t('conseiller.t13')} autoComplete="email" data-testid="teams-field-email" type="email" style={{ "width": "100%", "boxSizing": "border-box", "padding": "11px 14px", "border": "1px solid #ECECEA", "borderRadius": "10px", "background": "rgb(250, 249, 247)", "fontFamily": "inherit", "fontSize": "14px", "color": "rgb(10, 10, 15)", "outline": "none" }} value={form.email} onChange={setField('email')} />
                        </div>
                      </div>
                      <div style={{ "display": "flex", "flexDirection": "column", "gap": "6px" }}>
                        <label htmlFor="tp-phone" style={{ "fontSize": "12.5px", "fontWeight": "600", "letterSpacing": "-0.005em", "color": "rgb(55, 65, 81)" }}>
                          {t('conseiller.t14')}
                        </label>
                        <input id="tp-phone" className="tp-input" placeholder={t('conseiller.t15')} autoComplete="tel" inputMode="tel" data-testid="teams-field-phone" type="tel" style={{ "width": "100%", "boxSizing": "border-box", "padding": "11px 14px", "border": "1px solid #ECECEA", "borderRadius": "10px", "background": "rgb(250, 249, 247)", "fontFamily": "inherit", "fontSize": "14px", "color": "rgb(10, 10, 15)", "outline": "none" }} value={form.phone} onChange={setField('phone')} />
                      </div>
                      <div style={{ "display": "flex", "flexDirection": "column", "gap": "6px" }}>
                        <span id="tp-headcount-label" style={{ "fontSize": "12.5px", "fontWeight": "600", "letterSpacing": "-0.005em", "color": "rgb(55, 65, 81)" }}>
                          {t('conseiller.t16')}
                        </span>
                        <div role="radiogroup" aria-labelledby="tp-headcount-label" style={{ "display": "grid", "gridTemplateColumns": "repeat(4, 1fr)", "gap": "8px" }}>
                          <button type="button" role="radio" aria-checked={form.headcount === "10-50" ? "true" : "false"} data-testid="teams-headcount-10-50" style={headcountStyle("10-50")} onClick={() => pickHeadcount("10-50")}>
                            {t('conseiller.t17')}
                          </button>
                          <button type="button" role="radio" aria-checked={form.headcount === "50-200" ? "true" : "false"} data-testid="teams-headcount-50-200" style={headcountStyle("50-200")} onClick={() => pickHeadcount("50-200")}>
                            {t('conseiller.t18')}
                          </button>
                          <button type="button" role="radio" aria-checked={form.headcount === "200-1000" ? "true" : "false"} data-testid="teams-headcount-200-1000" style={headcountStyle("200-1000")} onClick={() => pickHeadcount("200-1000")}>
                            {t('conseiller.t19')}
                          </button>
                          <button type="button" role="radio" aria-checked={form.headcount === "1000+" ? "true" : "false"} data-testid="teams-headcount-1000+" style={headcountStyle("1000+")} onClick={() => pickHeadcount("1000+")}>
                            {t('conseiller.t20')}
                          </button>
                        </div>
                      </div>
                      <div style={{ "display": "flex", "flexDirection": "column", "gap": "6px" }}>
                        <label htmlFor="tp-message" style={{ "fontSize": "12.5px", "fontWeight": "600", "letterSpacing": "-0.005em", "color": "rgb(55, 65, 81)" }}>
                          {t('conseiller.t21')}
                          <span style={{ "fontWeight": "400", "color": "rgb(156, 163, 175)" }}>
                            {t('conseiller.t22')}
                          </span>
                        </label>
                        <textarea id="tp-message" className="tp-input" rows={3} placeholder={t('conseiller.t23')} data-testid="teams-field-message" style={{ "width": "100%", "padding": "11px 14px", "border": "1px solid #ECECEA", "borderRadius": "10px", "background": "rgb(250, 249, 247)", "fontFamily": "inherit", "fontSize": "14px", "color": "rgb(10, 10, 15)", "outline": "none", "resize": "vertical" }} value={form.message} onChange={setField('message')} />
                      </div>
                      {errorText && <FormError text={errorText} />}
                      <button type="submit" className="tp-submit" data-testid="teams-form-submit" style={{ ...{ "width": "100%", "padding": "14px", "background": "rgb(26, 86, 219)", "color": "rgb(255, 255, 255)", "borderWidth": "medium", "borderStyle": "none", "borderColor": "currentcolor", "borderImage": "none", "borderRadius": "12px", "fontFamily": "inherit", "fontSize": "15.5px", "fontWeight": "600", "letterSpacing": "-0.01em", "cursor": "pointer", "opacity": "1", "boxShadow": "rgba(26, 86, 219, 0.2) 0px 4px 16px", "transition": "0.2s" }, opacity: status === 'loading' ? 0.6 : 1 }} disabled={status === 'loading'}>
                        {t('conseiller.t24')}
                      </button>
                    </>
                  )}
                  <div style={{ "textAlign": "center", "fontSize": "12.5px", "fontWeight": "500", "color": "rgb(156, 163, 175)" }}>
                    {t('conseiller.t25')}
                  </div>
                </div>
              </form>
            </div>
            {"\n"}
          </div>
        </section>
        <section id="faq" className="tp-sec" style={{ "padding": "48px 40px", "background": "#FFFFFF", "borderTop": "1px solid #ECECEA", "scrollMarginTop": "64px", "paddingBottom": "80px" }}>
          <div style={{ "maxWidth": "1120px", "margin": "0px auto" }}>
            {"\n"}
            <div style={{ "maxWidth": "760px", "margin": "0px auto" }}>
              <div style={{ "textAlign": "center", "marginBottom": "28px" }}>
                <h2 style={{ "fontFamily": "'Inter Tight', Inter, -apple-system, sans-serif", "fontSize": "clamp(30px, 3.4vw, 42px)", "fontWeight": "700", "letterSpacing": "-0.034em", "lineHeight": "1.08", "margin": "0px", "maxWidth": "none", "color": "#0A0A0F", "textWrap": "balance" }}>
                  {t('faq.t01')}
                </h2>
              </div>
              <div style={{ "display": "flex", "flexDirection": "column", "gap": "10px" }}>
                {"\n"}
                <details className="tp-faqd" data-testid="teams-faq-0" style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "overflow": "hidden" }}>
                  <summary style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "gap": "14px", "padding": "18px 22px", "cursor": "pointer", "listStyle": "none" }}>
                    <span style={{ "fontSize": "15.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "#0A0A0F" }}>
                      {t('faq.t02')}
                    </span>
                    <span className="tp-faq-chev" style={{ "flex": "0 0 auto", "display": "flex", "color": "#6B7280", "transition": "transform 0.2s" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </span>
                  </summary>
                  <div style={{ "padding": "0px 22px 18px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.65", "color": "#4B5563" }}>
                    {t('faq.t03')}
                  </div>
                </details>
                {"\n"}
                <details className="tp-faqd" data-testid="teams-faq-1" style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "overflow": "hidden" }}>
                  <summary style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "gap": "14px", "padding": "18px 22px", "cursor": "pointer", "listStyle": "none" }}>
                    <span style={{ "fontSize": "15.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "#0A0A0F" }}>
                      {t('faq.t04')}
                    </span>
                    <span className="tp-faq-chev" style={{ "flex": "0 0 auto", "display": "flex", "color": "#6B7280", "transition": "transform 0.2s" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </span>
                  </summary>
                  <div style={{ "padding": "0px 22px 18px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.65", "color": "#4B5563" }}>
                    {t('faq.t05')}
                  </div>
                </details>
                {"\n"}
                <details className="tp-faq-more" style={{ "display": "contents" }}>
                  <summary style={{ "listStyle": "none", "cursor": "pointer", "display": "flex", "justifyContent": "center", "marginTop": "6px" }}>
                    <span className="tp-faq-more-btn" style={{ "display": "inline-flex", "alignItems": "center", "gap": "8px", "background": "#FFFFFF", "color": "#1A56DB", "border": "1px solid #BFDBFE", "borderRadius": "999px", "padding": "10px 18px", "fontSize": "14px", "fontWeight": "600" }}>
                      <span className="tp-faq-more-open">
                        {t('faq.t06')}
                      </span>
                      <span className="tp-faq-more-close">
                        {t('faq.t07')}
                      </span>
                      <svg className="tp-faq-more-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </span>
                  </summary>
                  {"\n"}
                  <details className="tp-faqd" data-testid="teams-faq-2" style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "overflow": "hidden" }}>
                    <summary style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "gap": "14px", "padding": "18px 22px", "cursor": "pointer", "listStyle": "none" }}>
                      <span style={{ "fontSize": "15.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "#0A0A0F" }}>
                        {t('faq.t08')}
                      </span>
                      <span className="tp-faq-chev" style={{ "flex": "0 0 auto", "display": "flex", "color": "#6B7280", "transition": "transform 0.2s" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </span>
                    </summary>
                    <div style={{ "padding": "0px 22px 18px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.65", "color": "#4B5563" }}>
                      {t('faq.t09')}
                    </div>
                  </details>
                  {"\n"}
                  <details className="tp-faqd" data-testid="teams-faq-3" style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "overflow": "hidden" }}>
                    <summary style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "gap": "14px", "padding": "18px 22px", "cursor": "pointer", "listStyle": "none" }}>
                      <span style={{ "fontSize": "15.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "#0A0A0F" }}>
                        {t('faq.t10')}
                      </span>
                      <span className="tp-faq-chev" style={{ "flex": "0 0 auto", "display": "flex", "color": "#6B7280", "transition": "transform 0.2s" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </span>
                    </summary>
                    <div style={{ "padding": "0px 22px 18px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.65", "color": "#4B5563" }}>
                      {t('faq.t11')}
                    </div>
                  </details>
                  {"\n"}
                  <details className="tp-faqd" data-testid="teams-faq-4" style={{ "background": "#FFFFFF", "border": "1px solid #ECECEA", "borderRadius": "16px", "overflow": "hidden" }}>
                    <summary style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "gap": "14px", "padding": "18px 22px", "cursor": "pointer", "listStyle": "none" }}>
                      <span style={{ "fontSize": "15.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "#0A0A0F" }}>
                        {t('faq.t12')}
                      </span>
                      <span className="tp-faq-chev" style={{ "flex": "0 0 auto", "display": "flex", "color": "#6B7280", "transition": "transform 0.2s" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </span>
                    </summary>
                    <div style={{ "padding": "0px 22px 18px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.65", "color": "#4B5563" }}>
                      {t('faq.t13')}
                    </div>
                  </details>
                  {"\n"}
                </details>
                {"\n"}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooterV2 />
    </div>
  );
}
